import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { getDefaultExploreBridge, getExploreBridge } from '../data/exploreBridges';
import { useMetaTags } from '../hooks/useMetaTags';
import { buildClicksSearchUrl } from '../utils/clicksSearchUrl';
import { BrandedExploreBridge } from '../components/BrandedExploreBridge';
import { getUniversityBySlug } from '../data/universityData';

// Ad-copy bridge template served at /explore/{brand}-{version}
// (e.g. /explore/UAGC-v1). Layout is fixed across every template:
// site nav → hero call to action → short paragraph section → footer.
// The copy, imagery, and outbound destination come from data/exploreBridges.
export function ExploreBridgePage() {
  const {
    bridgeSlug
  } = useParams<{
    bridgeSlug: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Fall back to the path segment so statically-registered routes (which
  // have no :bridgeSlug param) resolve the same as dynamic ones.
  const slug = bridgeSlug || location.pathname.replace(/\/+$/, '').split('/').filter(Boolean).pop();
  // Previewing the dynamic route directly (path shown as "/explore/:bridgeSlug")
  // has no real slug, so show the first template rather than a dead end.
  const isPlaceholderSlug = !slug || slug.startsWith(':');
  const bridge = isPlaceholderSlug ? getDefaultExploreBridge() : getExploreBridge(slug);
  useMetaTags({
    title: bridge?.metaTitle || 'Explore Online Degrees | FindOnlineDegrees',
    description: bridge?.metaDescription || 'Explore accredited online degree programs from our partner schools.',
    ogImage: bridge?.heroImage,
    // Ad-copy pages shouldn't compete with organic pages in search.
    robots: 'noindex, follow'
  });
  const handleNavigate = (page: string) => {
    navigate(page === 'compare' ? '/' : `/${page}`);
    window.scrollTo(0, 0);
  };
  // Carry any lead data and tracking params on this page's URL through to
  // the destination so the click can be attributed and the user isn't
  // asked for information they already provided.
  const handleCta = () => {
    if (!bridge) return;
    if (bridge.clicksSearch) {
      const url = buildClicksSearchUrl(location.search, bridge.clicksSearch);
      console.log('[Explore Bridge] redirecting to:', url);
      window.location.href = url;
      return;
    }
    if (!bridge.destinationUrl) return;
    const search = location.search.replace(/^\?/, '');
    const isExternal = /^https?:\/\//i.test(bridge.destinationUrl);
    const separator = bridge.destinationUrl.includes('?') ? '&' : '?';
    const destination = search ? `${bridge.destinationUrl}${separator}${search}` : bridge.destinationUrl;
    if (isExternal) {
      window.location.href = destination;
      return;
    }
    navigate(destination);
    window.scrollTo(0, 0);
  };
  // Branded ad copy renders the advertiser-styled layout instead.
  const university = bridge?.universitySlug ? getUniversityBySlug(bridge.universitySlug) : undefined;
  if (bridge && bridge.layout === 'branded' && university) {
    return <BrandedExploreBridge bridge={bridge} university={university} onCta={handleCta} data-id="element-1212" data-mp="2y6nosxkcxyfc" />;
  }
  if (!bridge) {
    return <div className="min-h-screen bg-white flex flex-col" data-id="element-1213" data-mp="2t5zp7ni2c99i">
        <Header currentPage="explore" onNavigate={handleNavigate} data-id="element-1214" data-mp="3qutd1uame5jx" />
        <main className="flex-grow flex items-center justify-center px-4 py-24" data-id="element-1215" data-mp="3lx18o0ssgqyu">
          <div className="text-center max-w-md" data-id="element-1216" data-mp="3lux91v1xae4d">
            <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1217" data-mp="3gwncph4gp7h6">
              This page isn't available
            </h1>
            <p className="text-gray-600 mb-8" data-id="element-1218" data-mp="3byc1p32fkqxd">
              The link you followed may be expired or mistyped. Explore our
              accredited online programs instead.
            </p>
            <button onClick={() => handleNavigate('compare')} className="px-6 py-3 bg-[#e8594f] text-white font-bold rounded-lg hover:bg-[#d54a41] transition-colors" data-id="element-1219" data-mp="20hvhkwfxlm46">
              Browse Online Degrees
            </button>
          </div>
        </main>
        <Footer onNavigate={handleNavigate} data-id="element-1220" data-mp="3fgradufom05s" />
      </div>;
  }
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-1221" data-mp="3kdq78kqqcl7o">
      <Header currentPage="explore" onNavigate={handleNavigate} data-id="element-1222" data-mp="3kc6n2j1ydq0m" />
      <main className="flex-grow" data-id="element-1223" data-mp="bz96fy315dan">
        {/* Hero + call to action */}
        <section className="relative border-b-4 border-[#e8594f]" data-id="element-1224" data-mp="gy2lvw1vnedg">
          <img src={bridge.heroImage} alt={bridge.heroImageAlt} className="absolute inset-0 w-full h-full object-cover" data-id="element-1225" data-mp="20jxiut4336j" />
          <div className="absolute inset-0 bg-gray-900/40 lg:bg-gray-900/20" aria-hidden="true" data-id="element-1226" data-mp="6xs7w980q2x8" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-id="element-1227" data-mp="2rpuc3kgt71d0">
            <div className="lg:grid lg:grid-cols-2 lg:gap-12" data-id="element-1228" data-mp="19s8t12jwuz4f">
              <div className="hidden lg:block" aria-hidden="true" data-id="element-1229" data-mp="19qnz9f5f7k7m" />
              <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10 text-center max-w-xl mx-auto lg:mx-0" data-id="element-1230" data-mp="14txhmndywgk3">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" data-id="element-1231" data-mp="1irvshit2xuuu">
                  {bridge.cardHeadline}
                </h1>
                <p className="mt-4 text-lg font-semibold text-[#e8594f]" data-id="element-1232" data-mp="r1r3f6udkjna">
                  {bridge.cardSubhead}
                </p>
                <button onClick={handleCta} className="mt-8 w-full sm:w-auto px-10 py-4 bg-[#e8594f] text-white text-lg font-bold rounded-lg shadow-md hover:bg-[#d54a41] hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8594f]" data-id="element-1233" data-mp="m3fv50jqah76">
                  {bridge.ctaLabel}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Short paragraph section */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center" data-id="element-1234" data-mp="22hopg4xbzh1a">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8" data-id="element-1235" data-mp="27f82j1ialnrn">
            {bridge.sectionHeading}
          </h2>
          <div className="space-y-6" data-id="element-1236" data-mp="2c788pboyhcwo">
            {bridge.paragraphs.map(paragraph => <p key={paragraph.slice(0, 40)} className="text-gray-600 leading-relaxed" data-id="element-1237" data-mp="2h4hywtk65slk">
                {paragraph}
              </p>)}
          </div>
          <div className="mt-10" data-id="element-1238" data-mp="1ohdyl1w0u2g4">
            <button onClick={handleCta} className="px-10 py-4 bg-[#e8594f] text-white text-lg font-bold rounded-lg shadow-md hover:bg-[#d54a41] hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#e8594f]" data-id="element-1239" data-mp="1tg8ftzecbiy4">
              {bridge.ctaLabel}
            </button>
          </div>
          {bridge.disclosure && <p className="mt-10 text-xs text-gray-400 leading-relaxed" data-id="element-1240" data-mp="y755aluvnk4n">
              {bridge.disclosure}
            </p>}
        </section>
      </main>
      <Footer onNavigate={handleNavigate} data-id="element-1241" data-mp="t8k3ihzz8se3" />
    </div>;
}